import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CartContext = createContext(null);

export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "mhsl_cart";

const itemKey = (meal_id, config, addons) => {
  const base = config ? `${meal_id}|${JSON.stringify(config)}` : meal_id;
  return addons && addons.length ? `${base}|+${[...addons].sort().join(",")}` : base;
};

const loadCart = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
  } catch (e) {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [items, setItems] = useState(loadCart);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const totalQty = items.reduce((s, i) => s + i.quantity, 0);

  const refreshPreview = useCallback(async () => {
    if (!user || items.length === 0) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const payload = {
        items: items.map((i) => ({
          meal_id: i.meal_id,
          quantity: i.quantity,
          ...(i.config ? { config: i.config } : {}),
          ...(i.addons && i.addons.length ? { addons: i.addons } : {}),
        })),
      };
      const { data } = await api.post("/clover/preview", payload);
      setPreview(data);
    } catch (e) {
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  }, [items, user]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      refreshPreview();
    }, 250);
    return () => clearTimeout(debounceRef.current);
  }, [refreshPreview]);

  const addItem = useCallback((meal, quantity = 1, config = null, addons = null) => {
    const meal_id = meal.meal_id;
    const key = itemKey(meal_id, config, addons);
    setItems((prev) => {
      const existing = prev.find((i) => i.key === key);
      if (existing) {
        return prev.map((i) =>
          i.key === key ? { ...i, quantity: i.quantity + quantity } : i
        );
      }
      return [
        ...prev,
        {
          key,
          meal_id,
          quantity,
          config: config || null,
          addons: addons || [],
          name: meal.name,
          image_url: meal.image_url,
          price_cents: meal.price_cents,
        },
      ];
    });
  }, []);

  const setQuantity = useCallback((key, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.key !== key)
        : prev.map((i) => (i.key === key ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((key) => setItems((prev) => prev.filter((i) => i.key !== key)), []);

  const clearCart = useCallback(() => setItems([]), []);

  const reorder = useCallback((orderItems) => {
    const mapped = orderItems.map((oi) => ({
      key: itemKey(oi.meal_id, oi.config, oi.addon_keys),
      meal_id: oi.meal_id,
      quantity: oi.quantity,
      config: oi.config || null,
      addons: oi.addon_keys || [],
      name: oi.name,
      image_url: oi.image_url,
      price_cents: oi.unit_amount,
    }));
    setItems(mapped);
  }, []);

  const value = useMemo(
    () => ({
      items,
      totalQty,
      preview,
      previewing,
      addItem,
      setQuantity,
      removeItem,
      clearCart,
      reorder,
      refreshPreview,
    }),
    [items, totalQty, preview, previewing, addItem, setQuantity, removeItem, clearCart, reorder, refreshPreview]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
